import { useCallback, useMemo } from 'react'
import { collection, doc, orderBy, query, serverTimestamp } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { fbt } from 'fbtee'
import { db } from '../lib/firebase'
import { commitUpdates } from '../lib/firestore'
import { runMutation } from '../lib/firestoreMutation'
import { useFirestoreCollection } from './useFirestore'
import { useAuth } from './useAuth'
import { useAdminPendingDocs } from './useAdminPendingDocs'
import type { RegionRequest, RegionRequestStatus } from '../types'

const EMPTY: RegionRequest[] = []

// Every region request, newest first. Admin-only: rules let a user read their
// own requests, but this subscribes to the whole collection, so non-admins run
// no query at all rather than hitting a permission error.
export function useAdminRegionRequests() {
  const { isAdmin, user } = useAuth()

  const requestsQuery = useMemo(
    () =>
      isAdmin
        ? (query(
            collection(db, 'regionRequests'),
            orderBy('createdAt', 'desc'),
          ) as Query<RegionRequest>)
        : null,
    [isAdmin],
  )

  const { data, loading, error } = useFirestoreCollection<RegionRequest>(requestsQuery)

  // Triage is per metro, not per submission: twelve people asking for Denver is
  // one decision, so the queue resolves a whole aggregated row at once.
  const resolveRequests = useCallback(
    async (ids: string[], status: RegionRequestStatus) => {
      const uid = user?.uid
      if (!uid || ids.length === 0) return
      await runMutation(
        fbt(
          'Could not update the request',
          'Error toast when an admin fails to triage a region request',
        ),
        () =>
          commitUpdates(
            db,
            ids.map(id => ({
              ref: doc(db, 'regionRequests', id),
              data: { status, resolvedAt: serverTimestamp(), resolvedBy: uid },
            })),
          ),
      )
    },
    [user?.uid],
  )

  return { requests: isAdmin ? data : EMPTY, loading, error, resolveRequests }
}

export function useAdminPendingRegionRequestCount(): number {
  return useAdminPendingDocs<RegionRequest>('regionRequests').length
}
